import { FC, useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useModal } from "utils/hookedComponents/Modal/core/modal.hook";
import { Context } from "./core/home.context";
import { handleCTAClick, isSupportedHomeObjective } from "./core/home.helpers";
import { IContext } from "./core/home.types";
import { useSelector, useDispatch } from "react-redux";
import { IConfigurationState } from "redux/configuration/configuration.interface";
import Layout from "components/Layout";
import Carousel from "components/Carousel";
import Button from "components/Inputs/Button";
import SpinGame from "components/SpinGame/SpinGame";
import { Trans, useTranslation } from "react-i18next";
import { Icons } from "utils/constants/icons";
import { useInternationalization } from "utils/hooks/useInternationalization";
import { deviceIsMobile, isDev } from "utils";
import i18n from "utils/internationalization";
import { useColor } from "utils/hooks/useColor";
import Winner from "components/Winner";
import Desktop from "pages/Desktop";
import CarouselAutoSwiping from "components/Carousel/composition/CarouselAutoSwiping";
import { useRedirect } from "utils/hooks/useRedirect";
import { setCanPlayAgain } from "redux/game/game.actions";
import { AppThunkDispatch } from "redux/configuration/configuration.types";
import {
  fetchConfiguration,
  fetchFormSubmitStatus,
} from "redux/configuration/configuration.actions";
import { setSession } from "redux/session/session.actions";
import AlreadyPlayedModal from "components/Modals/ModalAlreadyParticipated/AlreadyPlayedModal";
import { bootstrapGame } from "./core/home.bootstrap";

export { Context as HomeContext } from "./core/home.context";

const Home: FC = () => {
  const dispatch: AppThunkDispatch = useDispatch();
  const { isReady, configuration, winners, game, fromFilled } = useSelector(
    (state: IConfigurationState) => {
      return state;
    }
  );

  const { canPlayAgain } = game;
  const [isVisible, showModal, hideModal, reference] = useModal();
  const [isGameStarted, setIsGameStarted] = useState<boolean>(false);
  const [bootstrapStatus, setBootstrapStatus] = useState<
    "idle" | "loading" | "ready" | "error"
  >("idle");
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);

  const navigate = useNavigate();
  const { t } = useTranslation("home");
  const { entityId } = useParams();
  const isMobile = deviceIsMobile();

  useEffect(() => {
    if (bootstrapStatus !== "ready") return;
    if (!configuration || (!canPlayAgain?.c && !isDev())) return;
    if (configuration?.c?.ctid !== 2) return;
    if (!fromFilled) {
      navigate(`/${entityId}/form`);
    } else if (!configuration?.ojs || configuration?.ojs?.length === 0) {
      navigate(`/${entityId}/game`);
    }
  }, [
    navigate,
    fromFilled,
    configuration,
    entityId,
    canPlayAgain,
    bootstrapStatus,
  ]);

  useEffect(() => {
    if (entityId) {
      dispatch(fetchConfiguration(entityId));
    }
  }, [dispatch, entityId]);

  const locale = configuration?.c?.lg[0];
  const antiFraudType = configuration?.c?.aft;
  const requiresFormSubmitStatus = configuration?.c?.ctid === 2;

  useEffect(() => {
    if (!entityId || !locale) {
      return;
    }

    let isActive = true;
    setBootstrapStatus("loading");
    bootstrapGame({
      ensureSession: async () =>
        Boolean(await dispatch(setSession(locale, antiFraudType))),
      loadCanPlayAgain: async () => {
        if (!requiresFormSubmitStatus) {
          return Boolean(await dispatch(setCanPlayAgain(entityId)));
        }

        const [canPlayAgain, formSubmitStatus] = await Promise.all([
          dispatch(setCanPlayAgain(entityId)),
          dispatch(fetchFormSubmitStatus(entityId)),
        ]);
        return Boolean(canPlayAgain && formSubmitStatus);
      },
      isActive: () => isActive,
    })
      .then((isReady) => {
        if (isActive) {
          setBootstrapStatus(isReady ? "ready" : "error");
        }
      })
      .catch(() => {
        if (isActive) {
          setBootstrapStatus("error");
        }
      });

    return () => {
      isActive = false;
    };
  }, [
    dispatch,
    entityId,
    locale,
    antiFraudType,
    requiresFormSubmitStatus,
    bootstrapAttempt,
  ]);

  useEffect(() => {
    if (isReady && configuration) {
      const startGameTimeout = setTimeout(() => {
        setIsGameStarted(true);
      }, 500);

      return () => clearTimeout(startGameTimeout);
    }
    if (isReady && !configuration) {
      navigate("/404");
    }

    return undefined;
  }, [isReady, configuration, setIsGameStarted, navigate]);

  const context: IContext = {
    isVisible,
    showModal,
    hideModal,
    reference,
  };
  const hasEstablishments = Boolean(configuration?.ets?.length);
  const isBootstrapLoading =
    bootstrapStatus === "idle" || bootstrapStatus === "loading";
  const isAlreadyParticipated =
    bootstrapStatus === "ready" &&
    !hasEstablishments &&
    canPlayAgain?.c === false;
  const hasUnexpectedMissingObjective =
    bootstrapStatus === "ready" &&
    !hasEstablishments &&
    canPlayAgain?.c !== false &&
    !canPlayAgain?.obj &&
    configuration?.c?.ctid !== 2;
  const hasUnsupportedObjective =
    bootstrapStatus === "ready" &&
    !hasEstablishments &&
    configuration?.c?.ctid !== 2 &&
    canPlayAgain?.obj !== undefined &&
    !isSupportedHomeObjective(canPlayAgain.obj.t);
  const hasBootstrapError =
    bootstrapStatus === "error" ||
    hasUnexpectedMissingObjective ||
    hasUnsupportedObjective;
  const canNavigate =
    hasEstablishments || isSupportedHomeObjective(canPlayAgain?.obj?.t);
  const isCTADisabled =
    !isDev() &&
    (isBootstrapLoading ||
      isAlreadyParticipated ||
      (!canNavigate && !hasBootstrapError));
  const handleRetry = () => {
    setBootstrapAttempt((attempt) => attempt + 1);
  };

  useRedirect();
  useColor({ primaryColor: configuration?.s.pColor });
  useInternationalization({
    i18n,
    locale: configuration?.c?.lg[0],
  });

  console.log("configuration", configuration);
  console.log("can play again", canPlayAgain);
  // console.log("session", session);
  // console.log(
  //   "disabled button:",
  //   "ets:",
  //   configuration?.ets,
  //   "canPlayAgain:",
  //   canPlayAgain,
  //   "isDev:",
  //   isDev(),
  //   "Full disable value:",
  //   !configuration?.ets && canPlayAgain == null && !isDev()
  // );

  return (
    <Context.Provider value={context}>
      {isMobile ? (
        isReady &&
        configuration && (
          <Layout className="home">
            {winners.length > 0 && (
              <Carousel className="mb-7">
                <CarouselAutoSwiping speed={2350} />
                {winners.map((winner, index) => (
                  <Carousel.Item key={index}>
                    <Winner winner={winner} />
                  </Carousel.Item>
                ))}
              </Carousel>
            )}
            <div className="home__game-container f f-direction-column">
              <SpinGame isGameStarted={isGameStarted} />
              <h1 className="ft-up pt-7 f f-direction-column mb-2">
                <Trans
                  ns="home"
                  i18nKey="title"
                  components={[<span className="c-primary"></span>]}
                  values={{
                    restaurant: configuration?.dn || t("companyNameFallback"),
                  }}
                />
              </h1>
              <p className="mb-5 fw-500">
                {t("subtitle", { company: configuration?.c.name })}
              </p>
              <Button
                appearance="primary"
                text={t(
                  isBootstrapLoading
                    ? "loadingCTA"
                    : hasBootstrapError
                    ? "retryCTA"
                    : "CTA"
                )}
                onClick={
                  hasBootstrapError
                    ? handleRetry
                    : handleCTAClick(
                        navigate,
                        canPlayAgain?.obj?.t,
                        hasEstablishments ? configuration.ets : undefined
                      )
                }
                icon={Icons.chevronRight}
                disabled={isCTADisabled}
                loading={isBootstrapLoading}
              />
              {hasBootstrapError && (
                <p className="mt-3 c-error" role="alert">
                  {t("sessionError")}
                </p>
              )}
            </div>
            {!configuration?.s?.bkg && (
              <>
                <img
                  className="home__doodle home__doodle--1"
                  src="/static/images/doodles/doodle-1.png"
                  alt="doodle"
                />
                <img
                  className="home__doodle home__doodle--2"
                  src="/static/images/doodles/doodle-2.png"
                  alt="doodle"
                />
              </>
            )}
          </Layout>
        )
      ) : (
        <Desktop />
      )}
      {isAlreadyParticipated && <AlreadyPlayedModal />}
      <Outlet />
    </Context.Provider>
  );
};

export default Home;
